import { useCallback, useSyncExternalStore, type ReactNode } from "react";
import { locales, type Locale, type MessageKey } from "@gioia365/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { meQuery, useUpdateMe } from "../../api/queries";
import { useLocale } from "../../i18n/locale";
import "./select.css";
import "./public-shell.css";

type Props = {
  children: ReactNode;
  width?: "narrow" | "wide";
  /**
   * `false` per le pagine pubbliche che non sanno di chi sia la sessione aperta (l'invito: potrebbe essere di
   * un'altra persona): il selettore vale allora soltanto sul dispositivo, qualunque cosa ricordi la cache.
   */
  personLanguage?: boolean;
};

const localeLabels: Record<Locale, MessageKey> = { it: "common.italian", en: "common.english" };

// La cornice delle pagine fuori dal guscio dell'applicazione (accesso, reimpostazione, invito, «Scegli
// l'azienda», pagina d'errore fuori dal guscio), come `accesso.html` dei mock-up: marchio e lingua. Senza una
// sessione la scelta è del dispositivo e si ricorda (`setLocale`). Con una sessione (per esempio «Scegli
// l'azienda») la lingua è della persona: si salva con `PATCH /api/me`, come in «Il tuo account», altrimenti
// la scelta del dispositivo coprirebbe quella salvata finché l'identità non cambia. Dentro il guscio la
// lingua si sceglie soltanto in «Il tuo account». Le pagine che non sanno di chi sia la sessione (l'invito)
// chiedono `personLanguage={false}` e non toccano mai l'account di nessuno.
export function PublicShell({ children, width = "narrow", personLanguage = true }: Props) {
  const { locale, setLocale, t } = useLocale();
  const queryClient = useQueryClient();
  const updateMe = useUpdateMe();
  // Se c'è una sessione lo dice la cache: qui non si fanno richieste per saperlo, e le pagine senza sessione
  // (l'accesso) non ne perdono tempo. Chi la legge (guardie, pagina dell'invito) la mette in `["me"]`.
  const subscribe = useCallback((notify: () => void) => queryClient.getQueryCache().subscribe(notify), [queryClient]);
  const cachedSession = useSyncExternalStore(subscribe, () => queryClient.getQueryData(meQuery.queryKey) != null);
  const signedIn = personLanguage && cachedSession;
  // Mentre il salvataggio viaggia il selettore mostra già la scelta.
  const shown = (updateMe.isPending ? updateMe.variables.preferredLocale : null) ?? locale;

  function choose(next: Locale) {
    if (!signedIn) {
      setLocale(next);
      return;
    }
    // Se il salvataggio non riesce la scelta vale almeno su questo dispositivo, invece di non fare nulla.
    updateMe.mutate({ preferredLocale: next }, { onError: () => setLocale(next) });
  }

  return (
    <div className="g-public">
      <header className="g-public__header">
        <span className="g-public__brand">{t("common.appName")}</span>
        <select
          className="g-select g-public__locale"
          aria-label={t("common.language")}
          value={shown}
          onChange={(event) => {
            const next = locales.find((candidate) => candidate === event.target.value);
            if (next) choose(next);
          }}
        >
          {locales.map((code) => (
            <option key={code} value={code}>{t(localeLabels[code])}</option>
          ))}
        </select>
      </header>
      <main className="g-public__main">
        <div className="g-public__content" data-width={width}>{children}</div>
      </main>
    </div>
  );
}
